import MaterialIcon from '@material/react-material-icon';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { Button } from '@upmc/ims.component-library';
import { useFormik } from 'formik';
import RequiredIndicator from 'pages/ApplicationRequest/components/containers/RequiredIndicator';
import Yup from 'utils/validation/yup-extensions';
import Base from '../../Base/Base';
import Styles from './SSHException.module.scss';
import { SubmitSSHExceptionRequest } from './_api';

// Breakpoints for form fields
const GRID_BREAK_POINTS = { xs: 12, sm: 6, md: 6, lg: 4, xl: 4 };
const FULL_WIDTH_BREAK_POINTS = { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 };

/**
 * Reusable text field component that integrates with Formik for form handling.
 */
const CustomTextField = ({
	name,
	label,
	formik,
	multiline = false,
	rows = 1,
	disabled = false,
	required = false,
}) => (
	<TextField
		{...formik.getFieldProps(name)}
		label={
			<>
				{required && <RequiredIndicator />}
				{label}
			</>
		}
		fullWidth
		multiline={multiline}
		rows={rows}
		disabled={disabled}
		error={formik.touched[name] && Boolean(formik.errors[name])}
		helperText={formik.touched[name] && formik.errors[name]}
	/>
);

/**
 * Helper function to determine initial field value given whether the form is for another user or the current user.
 *
 * @param {boolean} isForOther. If true, the form is for another user. Othewrwise, the form is for the current user.
 * @param {*} userSelectedVal. Default value for requestee.
 * @param {*} userVal. Default value for current user.
 *
 * @returns Initial value for the field.
 */
const getInitialValue = (isForOther, userSelectedVal, userVal) => {
	if (isForOther) {
		return userSelectedVal || '';
	} else {
		return userVal || '';
	}
};

/**
 * Form component for submitting Outbound SSH Exception requests.
 *
 * @returns Form component.
 */
const SSHExceptionForm = ({
	user,
	userSelected,
	onFormSubmit,
	setTrackingNumber,
}) => {
	// Determine if submission is for another
	const isForOther =
		userSelected != null && userSelected.Requestee_ESOFIID !== user?.ID;

	// Formik for handling form input and validation
	const formik = useFormik({
		initialValues: {
			Name: getInitialValue(isForOther, userSelected?.Name, user?.Name),
			NTID: getInitialValue(
				isForOther,
				userSelected?.NTID,
				user?.NetworkID
			),
			ESignOn: getInitialValue(
				isForOther,
				userSelected?.ESignOn,
				user?.FamiliarESOID
			),
			Email: getInitialValue(
				isForOther,
				userSelected?.Email,
				user?.Email
			),
			Department: getInitialValue(
				isForOther,
				userSelected?.Department,
				user?.Department
			),
			Manager: getInitialValue(
				isForOther,
				userSelected?.Manager,
				user?.Manager
			),
			Requestee_ESOFIID: getInitialValue(
				isForOther,
				userSelected?.Requestee_ESOFIID,
				user?.ID
			),
			Requestor_ESOFIID: user?.ID || '',
			Justification: '',
			Confirmation: false,
		},
		validationSchema: Yup.object().shape({
			Justification: Yup.string().required('Justification is required.'),
			Confirmation: Yup.boolean().oneOf(
				[true],
				'You must acknowledge to proceed.'
			),
		}),
		onSubmit: async values => {
			const resp = await SubmitSSHExceptionRequest(values);
			if (!resp?.error) {
				setTrackingNumber(resp?.data?.payload);
			}
			onFormSubmit();
		},
	});

	// Text fields
	const textFields = [
		{ name: 'Name', label: 'Name' },
		{ name: 'NTID', label: 'Network ID' },
		{ name: 'ESignOn', label: 'ESignOn' },
		{ name: 'Email', label: 'Email' },
		{ name: 'Department', label: 'Department' },
		{ name: 'Manager', label: 'Manager' },
		{
			name: 'Justification',
			label: 'Justification',
			multiline: true,
			rows: 4,
			input: true,
			required: true,
		},
	];

	// Build and return React component
	return (
		<Base
			title={`Exception for ${isForOther ? 'Other' : 'Self'}`}
			className={Styles['base--default']}
		>
			<form onSubmit={formik.handleSubmit}>
				<Grid
					container
					direction='row'
					spacing={1}
					sx={{ alignItems: 'center' }}
				>
					{textFields.map(field => (
						<Grid
							item
							{...(field.input
								? FULL_WIDTH_BREAK_POINTS
								: GRID_BREAK_POINTS)}
						>
							<CustomTextField
								{...field}
								disabled={formik.isSubmitting || !field.input}
								formik={formik}
							/>
						</Grid>
					))}
					<Grid item {...FULL_WIDTH_BREAK_POINTS}>
						<FormControl>
							<FormControlLabel
								sx={{ marginBottom: '0' }}
								control={
									<Checkbox
										name='Confirmation'
										checked={formik.values.Confirmation}
										disabled={formik.isSubmitting}
										onChange={e => {
											formik.handleChange(e);
											formik.setFieldTouched(
												'Confirmation',
												true,
												false
											);
										}}
									/>
								}
								label={
									<>
										<RequiredIndicator />
										<span>
											I consent that for all Outbound SSH
											Exception requests, by myself and on
											behalf of others, will not include
											the following:{' '}
											<i>
												Protected Health Information
												(PHI), Personally Identifiable
												Information (PII) for employees
												or non-employees, or other
												confidential and/or proprietary
												data (i.e. account names and
												passwords, social security
												numbers, credit cards,
												contracts, UPMC financial data,
												Human Resources confidential
												data, defense and national
												security threats data, Homeland
												Security data, bioterrorism
												data, etc.)
											</i>
										</span>
									</>
								}
							/>
							<FormHelperText error>
								{formik.touched.Confirmation &&
									formik.errors.Confirmation}
							</FormHelperText>
						</FormControl>
					</Grid>
				</Grid>
				<Grid
					container
					direction='column'
					spacing={3}
					sx={{ alignItems: 'center' }}
				>
					<br />
					<Grid item>
						<Button
							type='submit'
							startIcon={
								formik.isSubmitting ? (
									<CircularProgress size={24} />
								) : (
									<MaterialIcon icon='save' />
								)
							}
							variant='contained'
							disabled={
								!formik.dirty ||
								!formik.isValid ||
								formik.isSubmitting
							}
							sx={{ margin: '5px' }}
							id='submit-form'
						>
							{formik.isSubmitting ? 'Submitting' : 'Submit'}
						</Button>
					</Grid>
				</Grid>
			</form>
		</Base>
	);
};

export default SSHExceptionForm;
